import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import bus from '@/core/bus';
import { ensure as pageReadyForScroll, cancel as cancelPendingWaiting } from './ensure';
import { routes } from './routes';
import { QueryKeys } from '@/shared/config/route-query.config';

preventAutoScroll();

export const SpaPageRenderedEventKey = 'SpaPageRendered';
export const SpaAwaitingPageEventKey = 'SpaAwaitingPage';
export const SpaForceConfigurationReloadEventKey = 'SpaForceConfigurationReload';

export const router = createRouter({
    history: createWebHistory((window as any).servercontext?.culture),
    routes: routes,
    scrollBehavior(to, from, savedPosition) {
        // Make sure an unfinished waiting to scroll is canceled first.
        cancelPendingWaiting();

        return new Promise((resolve) => {
            if (to.path === from.path || to.query[QueryKeys.SpaSilentUpdate]) {
                // Only query or params changed - don't scroll
                resolve();
                return;
            }

            if (to.meta.isContent) {
                // Content page - wait until rendered
                bus.once(SpaPageRenderedEventKey, () => handlePageRendered(to, resolve, savedPosition));
            } else {
                // Client page - scroll right away
                handlePageRendered(to, resolve, savedPosition);
            }
        });
    },
});

async function handlePageRendered(to: RouteLocationNormalized, resolve: Function, savedPosition: any) {
    if (to.hash) {
        resolve({ selector: to.hash });
        return;
    } else
    if (!savedPosition) {
        // New page with no hash - scroll to top
        resolve({ left: 0, top: 0 });
        return;
    }

    // We have a saved position. New SPA page is now loaded but async elements may not be there yet, so page could be too short
    try {
        const position = await pageReadyForScroll(
            () => document.body.clientHeight >= (savedPosition as any).top + window.innerHeight,
            savedPosition);
        resolve(position);
    } catch (e) {
        // Timeout or cancelled - dont scroll
        resolve();
    }
}

function preventAutoScroll() {
    if ('scrollRestoration' in history) {
        try {
            history.scrollRestoration = 'manual';
        } catch (e) {
            // Ignore
        }
    }
}

// Potential initial scrolling to hash
bus.once(SpaPageRenderedEventKey, async() => {
    const hashElement = router.currentRoute.value.hash && document.querySelector(router.currentRoute.value.hash) as HTMLElement;
    if (hashElement) {
        hashElement.scrollIntoView();
    }
});
